import { brand } from '@alison/ui';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { InsightsCharts, VIOLET_TINT } from './InsightsCharts.js';
import { Pill } from './Pill.js';
import {
  type Finding,
  type Recommendation,
  type ReportResult,
  type ReportScope,
  scopeText,
} from './reportResult.js';

const NUMBER_SIZE = 32;
const PRIORITY_TONE: Record<string, { bg: string; color: string }> = {
  high: { bg: 'error.bg', color: 'error.main' },
  medium: { bg: 'warning.bg', color: 'warning.main' },
  low: { bg: 'success.bg', color: 'success.main' },
};

function SectionLabel({ children }: { children: ReactNode }): ReactNode {
  return (
    <Typography
      sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}
    >
      {children}
    </Typography>
  );
}

function Card({ children }: { children: ReactNode }): ReactNode {
  return (
    <Stack
      sx={{
        gap: 1,
        p: 4,
        borderRadius: 1.2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Stack>
  );
}

function ScopeLine({ scope }: { scope: ReportScope }): ReactNode {
  const text = scopeText(scope);
  if (text === null) return null;
  return <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{text}</Typography>;
}

function FindingCard({ finding }: { finding: Finding }): ReactNode {
  return (
    <Card>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{finding.title}</Typography>
      {finding.evidence === null || finding.evidence === undefined ? null : (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{finding.evidence}</Typography>
      )}
      {finding.context === null || finding.context === undefined ? null : (
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{finding.context}</Typography>
      )}
    </Card>
  );
}

// Read-only: what to do next is advice, not an action this panel takes.
function RecommendationRow({
  recommendation,
  number,
}: {
  recommendation: Recommendation;
  number: number;
}): ReactNode {
  const priority = recommendation.priority ?? null;
  const tone = priority === null ? undefined : PRIORITY_TONE[priority.toLowerCase()];

  return (
    <Stack
      direction="row"
      data-testid="report-recommendation"
      sx={{
        gap: 3,
        px: 4,
        py: 3,
        borderRadius: 1.2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          width: NUMBER_SIZE,
          height: NUMBER_SIZE,
          flexShrink: 0,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: VIOLET_TINT,
          color: brand.violet,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {number}
      </Box>
      <Stack sx={{ flex: 1, minWidth: 0, gap: 1 }}>
        <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{recommendation.action}</Typography>
          {priority === null ? null : (
            <Pill sx={tone === undefined ? {} : { bgcolor: tone.bg, color: tone.color }}>
              {priority.toUpperCase()}
            </Pill>
          )}
        </Stack>
        {recommendation.rationale === null || recommendation.rationale === undefined ? null : (
          <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: 'text.secondary' }}>
            {recommendation.rationale}
          </Typography>
        )}
        {recommendation.expected_impact === null ||
        recommendation.expected_impact === undefined ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
            {recommendation.expected_impact}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

/** `labels` are the service's section headings (ActionDisplay.labels), by key. */
export function ReportView({
  result,
  labels = {},
}: {
  result: ReportResult;
  labels?: Record<string, string>;
}): ReactNode {
  const { report, scope, charts } = result;

  return (
    <Stack sx={{ gap: 5 }} data-testid="report-view">
      {report.summary === null || report.summary === undefined ? null : (
        <Stack sx={{ gap: 1 }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>{report.summary}</Typography>
          {scope === null ? null : <ScopeLine scope={scope} />}
        </Stack>
      )}

      {charts.length === 0 && report.key_findings.length === 0 ? null : (
        <Stack sx={{ gap: 2 }}>
          <SectionLabel>{labels['findings'] ?? 'KEY FINDINGS'}</SectionLabel>
          <InsightsCharts charts={charts} />
          {report.key_findings.map((finding) => (
            <FindingCard key={finding.title} finding={finding} />
          ))}
        </Stack>
      )}

      {report.recommendations.length === 0 ? null : (
        <Stack sx={{ gap: 2 }}>
          <SectionLabel>{labels['recommendations'] ?? 'RECOMMENDATIONS'}</SectionLabel>
          {report.recommendations.map((recommendation, index) => (
            <RecommendationRow
              key={recommendation.action}
              recommendation={recommendation}
              number={index + 1}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
